/**
 * CopyButton (spec 020 US1) — the shared copy-to-clipboard affordance for the
 * dense views (masked license keys, job/trace/device IDs). Copies a caller-given
 * FULL value (which may differ from the masked text shown in the cell), shows a
 * transient "Copied" confirmation on success that auto-reverts, and never claims
 * success when the clipboard is unavailable or denied (the value stays manually
 * selectable). Icon-only by default, but ALWAYS carries an accessible
 * label/tooltip per the design-system rule — the PORTALED one (F-10), the same `Tooltip`
 * every other row action uses, never a native `title`.
 *
 * R10 (WS-Z7): it is a `.btn`. `.copy-btn` used to re-implement the height, the padding, the
 * radius, the border, the background, the hover and the focus ring — a second button
 * vocabulary, and one that therefore missed every later change to the first. What the class
 * keeps is the only thing that is genuinely this control's: the success colour swap.
 */
import { useEffect, useRef, useState } from "react";
import { copyText } from "./clipboard.js";
import { Icon } from "./Icon.js";
import { Tooltip } from "./Tooltip.js";
import "./ui.css";

export interface CopyButtonProps {
  /** The complete value written to the clipboard (not the masked display form). */
  value: string;
  /** Accessible label + tooltip; also the success/idle hint. Defaults to a generic copy label. */
  label?: string;
  /** Optional visible text next to the icon (icon-only when omitted). */
  children?: React.ReactNode;
  className?: string;
  /** How long the "Copied" state stays before reverting (ms). */
  revertMs?: number;
  "data-testid"?: string;
}

export function CopyButton({ value, label = "Copy to clipboard", children, className, revertMs = 1400, "data-testid": testId }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending revert timer on unmount so we never setState after teardown.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const onClick = async () => {
    const ok = await copyText(value);
    if (!ok) return; // no false success — the value remains selectable
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), revertMs);
  };

  const tip = copied ? "Copied" : label;
  return (
    /* WS-Z9 / F-10: the SHARED tooltip, not a native `title`.
       This control is a ROW ACTION on the Observability log tables, standing beside a
       `RowAction` Trace in the same cell — and `RowAction`'s own header argues against
       `title` in as many words: a ~1s platform delay, no styling, and nothing at all on a
       touch device. Two icon buttons in one cell answering a hover in two different ways is
       the inconsistency rule 13 exists to remove. The bubble is a portal, which is also the
       only reason it is legible inside `.table-scroll`.
       `title` is REMOVED rather than kept beside it: two tooltips on one control is worse
       than either. The accessible name is unchanged, and still follows the copied state. */
    <Tooltip text={tip}>
      <button
        type="button"
        // Icon-only is a square `.btn--icon`; with a label it is an ordinary small button —
        // `.btn--icon` would crush the text into a 30×30 box.
        className={`btn btn--sm${children ? "" : " btn--icon"} copy-btn${className ? ` ${className}` : ""}`}
        data-testid={testId ?? "copy-button"}
        data-copied={copied}
        aria-label={tip}
        onClick={onClick}
      >
        <span className="copy-btn__icon" aria-hidden="true">{copied ? <Icon name="check" size={16} /> : <Icon name="copy" size={16} />}</span>
        {(copied || children) && <span className="copy-btn__label">{copied ? "Copied" : children}</span>}
      </button>
    </Tooltip>
  );
}
